import type { ServiceItem } from '../types';
import { contractedQuantityOfRow, isNepService, preferNonNep } from './nepPosition';
import {
    inspectionProofCount,
    inspectionProofMatcher,
    isFlatRatePosition,
    type InspectionProofLogLike,
} from './projectLv';
import { isAccountingEntry } from './serviceLogUtils';

/**
 * Begehungskennzahlen der Listen- und Verwaltungsansichten: Fortschritt auf
 * Projektkarte, Kanban und Kalender, Kontingent- und Liegezeitwarnung,
 * Vertretungsuebersicht.
 *
 * Seit 30.09.2026 bucht der Versand im Pauschalprojekt jede Begehung als
 * NACHWEISZEILE auf die Monatspauschale (Menge 0, 'NW-…'); die
 * Begehungsposition waechst nicht mehr. Jede dieser Stellen las bisher nur
 * quantityUsed bzw. die Protokollzeilen der Begehungspositionen — sie blieben
 * ab der Umstellung stehen (Balken, Kontingent) oder meldeten Liegezeit,
 * obwohl begangen wurde. Und eine Pauschale mit Titel „Begehung" oder
 * Zuordnung 'inspection' galt als Begehungsposition: ihre MONATE zaehlten als
 * Begehungen.
 *
 * Beides einmal hier, mit den Helfern aus projectLv (isFlatRatePosition,
 * inspectionProofCount/-Matcher), damit alle Ansichten dieselben Nachweise
 * meinen wie Projektcheck und Dashboard. Rein, ohne React — pruefbar ohne DOM.
 */

/** Eine Protokollzeile, so weit diese Kennzahlen sie lesen. */
export interface InspectionFigureLogLike extends InspectionProofLogLike {
    date?: string | null;
}

type Log = ReadonlyArray<InspectionFigureLogLike | null | undefined> | null | undefined;

/** Eine rohe project_services-Zeile (Projektliste), so weit hier gelesen. */
export interface InspectionRowLike {
    id?: string | null;
    assignment?: string | null;
    is_optional?: unknown;
    quantity_contracted?: unknown;
    quantity_used?: unknown;
    flat_rate?: unknown;
    flatRate?: unknown;
}

/** Begehungsposition der Projektliste: Zuordnung 'inspection', nie eine Pauschale. */
const isInspectionRow = (s: InspectionRowLike | null | undefined): s is InspectionRowLike =>
    !!s && s.assignment === 'inspection' && !isFlatRatePosition(s);

/**
 * Kanban und Kalender: Stand ueber ALLE Begehungspositionen, dazu die
 * Nachweise auf der Pauschale — sie sind Begehungen derselben Gruppe.
 */
export const inspectionRowsTotals = (
    rows: ReadonlyArray<InspectionRowLike | null | undefined> | null | undefined,
    serviceLog: Log,
): { used: number; contracted: number } => {
    let used = 0;
    let contracted = 0;
    for (const s of rows || []) {
        if (!isInspectionRow(s)) continue;
        contracted += contractedQuantityOfRow(s);
        used += Number(s.quantity_used) || 0;
    }
    // Ohne Vertragsmenge zeigt keine Ansicht einen Balken — dann das
    // Protokoll gar nicht erst durchgehen.
    if (!(contracted > 0)) return { used, contracted };
    return { used: used + inspectionProofCount(rows, serviceLog), contracted };
};

/**
 * Projektkarte: die TURNUSposition (hoechste Vertragsmenge unter den
 * Regelpositionen) mit den Nachweisen auf der Pauschale — dieselbe Zuordnung
 * wie der Dashboard-Gantt (dashboardModel).
 */
export const turnusRowProgress = (
    rows: ReadonlyArray<InspectionRowLike | null | undefined> | null | undefined,
    serviceLog: Log,
): { used: number; contracted: number; pct: number } | null => {
    const inspRows = (rows || []).filter(isInspectionRow);
    if (inspRows.length === 0) return null;
    // Unter den REGELpositionen (preferNonNep): eine NEP, auch eine viel
    // gebuchte, darf die Turnusposition nicht verdraengen.
    const candidates = preferNonNep(inspRows);
    const primary = candidates.reduce((best, cur) =>
        contractedQuantityOfRow(cur) > contractedQuantityOfRow(best) ? cur : best
    , candidates[0]);
    const contracted = contractedQuantityOfRow(primary);
    if (!(contracted > 0)) return null;
    const used = (Number(primary.quantity_used) || 0) + inspectionProofCount(rows, serviceLog);
    return { used, contracted, pct: Math.min(100, Math.round(used / contracted * 100)) };
};

/**
 * Kontingentwarnung (Meldungen und ihr Zaehler in der Verwaltung): die
 * Turnusposition — 2.7.10 oder Titel „Begehung", keine NEP, keine Pauschale —
 * und ihr Rest, Nachweise eingerechnet.
 *
 * Die Pauschale stand vor dem Ausschluss oft VOR 2.7.10 im LV und wurde von
 * `find` getroffen: gewarnt wurde dann ueber Monate statt Begehungen.
 */
export const inspectionQuota = (
    services: ReadonlyArray<ServiceItem> | null | undefined,
    serviceLog: Log,
): { used: number; contracted: number; remaining: number } | null => {
    const list = services || [];
    const turnus = list.find(s =>
        !isNepService(s) &&
        !isFlatRatePosition(s) &&
        (s.lvPosition === '2.7.10' || (s.title || '').toLowerCase().includes('begehung')),
    );
    if (!turnus) return null;
    const used = (Number(turnus.quantityUsed) || 0) + inspectionProofCount(list, serviceLog);
    const contracted = Number(turnus.quantityContracted) || 0;
    return { used, contracted, remaining: contracted - used };
};

/**
 * Liegezeitwarnung: das juengste Datum einer Begehungszeile im Protokoll —
 * Buchung auf einer Begehungsposition (ohne Buchhaltungsvorgaenge) ODER
 * Nachweis auf der Pauschale.
 *
 * Die Pauschale selbst ist keine Begehungsposition: ihre Monatsbuchungen
 * (FLAT-JJJJ-MM, am Monatsersten) galten sonst als Begehung und verschoben die
 * Liegezeit.
 */
export const latestInspectionLogDate = (
    services: ReadonlyArray<ServiceItem> | null | undefined,
    serviceLog: Log,
): Date | null => {
    const list = services || [];
    const ids = new Set(
        list
            .filter(s =>
                !isFlatRatePosition(s) &&
                (s.assignment === 'inspection' || s.lvPosition === '2.7.10' || (s.title || '').toLowerCase().includes('begehung')),
            )
            .map(s => s.id),
    );
    const isProof = inspectionProofMatcher(list);
    let latest: Date | null = null;
    for (const entry of serviceLog || []) {
        if (!entry?.date) continue;
        const counts = (!!entry.serviceId && ids.has(entry.serviceId) && !isAccountingEntry(entry as { bookingReference?: string })) || isProof(entry);
        if (!counts) continue;
        const d = new Date(entry.date);
        if (!isNaN(d.getTime()) && (!latest || d > latest)) latest = d;
    }
    return latest;
};

/**
 * Vertretungsuebersicht: zaehlt diese Protokollzeile als Begehung?
 *
 * Begehungsposition (Zuordnung 'inspection' oder 2.7.10, nie die Pauschale)
 * oder Nachweis auf der Pauschale. Ohne erkennbare Begehungsposition zaehlte
 * bisher jede Zeile; in einem reinen Pauschal-LV sind die Nachweise genau die
 * Begehungen — dort zaehlen nur sie, nicht die Monatsbuchungen.
 */
export const substitutionInspectionMatcher = (
    services: ReadonlyArray<ServiceItem> | null | undefined,
): ((entry: InspectionFigureLogLike | null | undefined) => boolean) => {
    const list = services || [];
    const ids = new Set(
        list
            .filter(s => !isFlatRatePosition(s) && (s.assignment === 'inspection' || s.lvPosition === '2.7.10'))
            .map(s => s.id),
    );
    const isProof = inspectionProofMatcher(list);
    const everyLine = ids.size === 0 && !list.some(s => isFlatRatePosition(s));
    return (entry) => isProof(entry) || everyLine || (!!entry?.serviceId && ids.has(entry.serviceId));
};
